import { useRef } from "react";
import { Link } from "react-router-dom";
import { m as motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { ResponsiveImage } from "./ResponsiveImage";
import { usePerformance } from "../lib/performance";

/* ---------- scroll reveal ---------- */
export const Reveal = ({ children, delay = 0, className = "", y = 28, ...props }) => {
  const { reducedEffects } = usePerformance();
  if (reducedEffects) return <div className={className} {...props}>{children}</div>;
  return (
  <motion.div
    {...props}
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
  );
};

/* ---------- masked line (hero signature) ---------- */
export const MaskLine = ({ children, delay = 0, className = "" }) => {
  const { reducedEffects } = usePerformance();
  if (reducedEffects) return <span className={`block ${className}`}>{children}</span>;
  return (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
  );
};

/* ---------- eyebrow label ---------- */
export const Eyebrow = ({ children, light = false, className = "" }) => (
  <p className={`font-mono text-[11px] uppercase tracking-[0.28em] ${light ? "text-leaf" : "text-tang"} ${className}`}>
    {children}
  </p>
);

/* ---------- section heading ---------- */
export const SectionHead = ({ eyebrow, title, sub, light = false, className = "" }) => (
  <div className={`max-w-3xl ${className}`}>
    <Reveal><Eyebrow light={light} className="mb-4">{eyebrow}</Eyebrow></Reveal>
    <Reveal delay={0.06}>
      <h2 className={`font-serif font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] ${light ? "text-cream" : "text-forest"}`}>
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.12}>
        <p className={`mt-5 text-base sm:text-lg leading-relaxed ${light ? "text-cream/70" : "text-sage"}`}>{sub}</p>
      </Reveal>
    )}
  </div>
);

/* ---------- slow editorial marquee ---------- */
export const Marquee = ({ items }) => {
  const row = items.map((it, i) => (
    <span key={i} className="flex items-center shrink-0">
      <span className="font-serif font-semibold uppercase tracking-wide text-forest text-xl sm:text-2xl px-8">{it}</span>
      <span className="h-2 w-2 rounded-full bg-tang shrink-0" />
    </span>
  ));
  return (
    <div className="overflow-hidden border-y border-parchment bg-kraft py-5" data-testid="marquee-band">
      <div className="marquee-track">
        <div className="flex shrink-0 items-center">{row}</div>
        <div className="flex shrink-0 items-center" aria-hidden="true">{row}</div>
      </div>
    </div>
  );
};

/* ---------- arch / clipped image frame ---------- */
export const ClipImg = ({ src, alt = "", className = "", imgClassName = "", arch = true, ...props }) => (
  <div className={`bg-white border border-parchment p-2.5 shadow-[0_24px_60px_-30px_rgba(16,41,26,0.35)] ${arch ? "rounded-t-[140px]" : "rounded-sm"} ${className}`}>
    <div className={`overflow-hidden ${arch ? "rounded-t-[130px]" : "rounded-[2px]"}`}>
      <ResponsiveImage src={src} alt={alt} className={`w-full h-auto object-cover ${imgClassName}`} {...props} />
    </div>
  </div>
);

/* ---------- subtle parallax wrapper ---------- */
const AnimatedParallax = ({ children, className, distance }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);
  return (
    <div ref={ref} className={`relative ${className}`}>
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
};

export const Parallax = ({ children, className = "", distance = 50 }) => {
  const { reducedEffects } = usePerformance();
  return reducedEffects ? <div className={className}>{children}</div>
    : <AnimatedParallax className={className} distance={distance}>{children}</AnimatedParallax>;
};

/* ---------- page hero (inner pages) ---------- */
export const PageHero = ({ eyebrow, title, sub, img, children }) => (
  <header className="relative bg-forest-deep text-cream overflow-hidden">
    <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#FAF7F0 0.7px, transparent 0.7px)", backgroundSize: "18px 18px" }} />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-20 lg:pt-44 lg:pb-24 grid lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-7">
        <Reveal><Eyebrow light className="mb-5">{eyebrow}</Eyebrow></Reveal>
        <MaskLine delay={0.1}>
          <h1 className="font-serif font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{title}</h1>
        </MaskLine>
        {sub && (
          <Reveal delay={0.25}>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-cream/70 leading-relaxed">{sub}</p>
          </Reveal>
        )}
        {children}
      </div>
      {img && (
        <div className="lg:col-span-5 hidden sm:block">
          <Reveal delay={0.2} y={40}>
            <Parallax distance={26}>
              <ClipImg src={img} alt="" className="rotate-2" />
            </Parallax>
          </Reveal>
        </div>
      )}
    </div>
  </header>
);

/* ---------- bottom CTA band ---------- */
export const CTASection = ({ title = "Ready to source from Hyderabad?", sub = "Send your requirement — product, quantity and destination port. Every RFQ gets a personal reply from the export desk." }) => (
  <section className="bg-kraft border-t border-parchment">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
      <div className="max-w-2xl">
        <Reveal><Eyebrow className="mb-4">Next step</Eyebrow></Reveal>
        <Reveal delay={0.06}>
          <h2 className="font-serif font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-forest leading-[1.05]">{title}</h2>
        </Reveal>
        <Reveal delay={0.12}><p className="mt-4 text-sage leading-relaxed">{sub}</p></Reveal>
      </div>
      <Reveal delay={0.15} className="flex flex-col sm:flex-row gap-4">
        <Link
          to="/request-quote"
          data-testid="cta-request-quote-btn"
          className="inline-flex items-center justify-center gap-2 bg-forest text-cream px-8 py-4 font-semibold text-sm tracking-wide hover:bg-forest-deep hover:-translate-y-0.5 transition-all duration-300"
        >
          Request a Quote <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/track"
          data-testid="cta-track-shipment-btn"
          className="inline-flex items-center justify-center gap-2 border border-forest/30 text-forest px-8 py-4 font-semibold text-sm tracking-wide hover:bg-white transition-all duration-300"
        >
          Track a shipment <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);
